import { Trans, useLingui } from '@lingui/react';
import { Alert } from '@startuptools/ui/Alert';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Table } from '@startuptools/ui/Table';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { GqlArticlesOfAssoc, GqlWarrantProgram } from '../../../../../graphql/base-types.graphql';
import { toDateTime } from '@startuptools/common/common';
import { wpEditOptionsSubsLink } from '../../../../../components/Links';
import BigNumber from 'bignumber.js';
import { useMemo } from 'react';
import { useNavigate } from '../../../../../components/Navigate';
import { Link } from 'react-router-dom';

export const WpStepSummary = ({
  warrantProgram,
}: {
  warrantProgram: Pick<
    GqlWarrantProgram,
    | 'id'
    | 'warrantKind'
    | 'usesStepper'
    | 'illiquidityDiscount'
    | 'pricePerWarrant'
    | 'discountPricePerWarrant'
    | 'strikePrice'
    | 'warrantStartDate'
    | 'warrantEndDate'
    | 'shareSubscribeStartDate'
    | 'shareSubscribeEndDate'
    | 'payBeforeDate'
    | 'warrantSubscribeEndDate'
    | 'useRiksbankenRate'
    | 'riskfreeInterestRate'
    | 'volatility'
    | 'valuationCompanyValue'
    | 'valuationNumberOfShares'
    | 'warrantsForQeso'
  > & {
    articlesOfAssoc?: Pick<
      GqlArticlesOfAssoc,
      'id' | 'numberOfSharesBefore' | 'numberOfSharesAfter' | 'changeNumberOfShares'
    > | null;
  };
}) => {
  const { i18n } = useLingui();
  const { link } = useNavigate();

  const valuationPricePerShare =
    warrantProgram.valuationCompanyValue && warrantProgram.valuationNumberOfShares
      ? new BigNumber(warrantProgram.valuationCompanyValue).div(warrantProgram.valuationNumberOfShares)
      : null;

  const increase = useMemo(() => {
    if (!valuationPricePerShare || !warrantProgram.strikePrice) {
      return 0;
    }
    const aoa = warrantProgram.articlesOfAssoc;
    if (aoa?.changeNumberOfShares && aoa.numberOfSharesBefore && aoa.numberOfSharesAfter) {
      const pricePerShareAfterSplit = new BigNumber(aoa.numberOfSharesBefore)
        .div(aoa.numberOfSharesAfter)
        .times(valuationPricePerShare);
      return new BigNumber(warrantProgram.strikePrice).minus(pricePerShareAfterSplit).div(pricePerShareAfterSplit);
    }
    return new BigNumber(warrantProgram.strikePrice).minus(valuationPricePerShare).div(valuationPricePerShare);
  }, [valuationPricePerShare, warrantProgram.articlesOfAssoc, warrantProgram.strikePrice]);

  return (
    <Stack gap={3}>
      <Typography level="h4">
        <Trans id="Summary" />
      </Typography>
      <Alert>
        <Trans id="Here is a summary. Before you proceed, you should verify that everything is correct" />
      </Alert>

      <Table sx={{ tableLayout: 'auto', maxWidth: 700 }}>
        <tbody>
          {!warrantProgram.warrantsForQeso && (
            <tr>
              <td>
                <Typography level="title-sm">
                  <Trans id="Share price at valuation" />:
                </Typography>
              </td>
              <td>
                {valuationPricePerShare && (
                  <>
                    <NumberFormat locale={i18n.locale} variant="currency" currency="SEK">
                      {valuationPricePerShare}
                    </NumberFormat>
                    {' / aktie'}
                  </>
                )}
              </td>
            </tr>
          )}

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Price per share at strike" />:
              </Typography>
            </td>
            <td>
              <NumberFormat
                locale={i18n.locale}
                variant="currency"
                currency={'SEK'}
                minimumFractionDigits={2}
                maximumFractionDigits={6}
              >
                {warrantProgram.strikePrice}
              </NumberFormat>
            </td>
          </tr>

          {!warrantProgram.warrantsForQeso && (
            <>
              <tr>
                <td>
                  <Typography level="title-sm">
                    <Trans id="Increase of strike compared to valuation" />:
                  </Typography>
                </td>
                <td>
                  <NumberFormat locale={i18n.locale} variant="percentage">
                    {increase}
                  </NumberFormat>
                </td>
              </tr>
              <tr>
                <td>
                  <Typography level="title-sm">
                    <Trans id="Price per option before discount" />:
                  </Typography>
                </td>
                <td>
                  <NumberFormat
                    locale={i18n.locale}
                    variant="currency"
                    currency={'SEK'}
                    minimumFractionDigits={0}
                    maximumFractionDigits={6}
                  >
                    {warrantProgram.pricePerWarrant}
                  </NumberFormat>
                </td>
              </tr>

              <tr>
                <td>
                  <Typography level="title-sm">
                    <Trans id="Illiquidity Discount" />:
                  </Typography>
                </td>
                <td>
                  <NumberFormat locale={i18n.locale} variant="percentage">
                    {warrantProgram.illiquidityDiscount}
                  </NumberFormat>
                </td>
              </tr>
            </>
          )}

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Duration" />:
              </Typography>
            </td>
            <td>
              <Trans
                id="{duration} months"
                values={{
                  duration:
                    warrantProgram.warrantStartDate && warrantProgram.warrantEndDate
                      ? Math.round(
                          toDateTime(warrantProgram.warrantEndDate)?.diff(
                            toDateTime(warrantProgram.warrantStartDate),
                            'months',
                            {
                              conversionAccuracy: 'longterm',
                            },
                          ).months ?? 0,
                        )
                      : 0,
                }}
              />
            </td>
          </tr>

          {!warrantProgram.warrantsForQeso && (
            <tr>
              <td>
                <Typography level="title-sm">
                  <Trans id="Volatility" />:
                </Typography>
              </td>
              <td>
                <NumberFormat locale={i18n.locale} variant="percentage">
                  {warrantProgram.volatility}
                </NumberFormat>
              </td>
            </tr>
          )}

          {warrantProgram.useRiksbankenRate && (
            <tr>
              <td>
                <Typography level="title-sm">
                  <Trans id="Risk free interest rate" />:
                </Typography>
              </td>
              <td>
                <NumberFormat
                  locale={i18n.locale}
                  variant="percentage"
                  minimumFractionDigits={4}
                  maximumFractionDigits={4}
                >
                  {warrantProgram.riskfreeInterestRate}
                </NumberFormat>
              </td>
            </tr>
          )}

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="The program starts" />:
              </Typography>
            </td>
            <td>
              <DateFormat locale={i18n.locale}>{warrantProgram.warrantStartDate}</DateFormat>
            </td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Last day to subscribe for warrants" />:
              </Typography>
            </td>
            <td>
              <DateFormat locale={i18n.locale}>{warrantProgram.warrantSubscribeEndDate}</DateFormat>
            </td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Last day to pay for the warrants" />:
              </Typography>
            </td>
            <td>
              <DateFormat locale={i18n.locale}>{warrantProgram.payBeforeDate}</DateFormat>
            </td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="First day to subscribe for shares" />:
              </Typography>
            </td>
            <td>
              <DateFormat locale={i18n.locale}>{warrantProgram.shareSubscribeStartDate}</DateFormat>
            </td>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Last day to subscribe for shares" />:
              </Typography>
            </td>
            <td>
              <DateFormat locale={i18n.locale}>{warrantProgram.shareSubscribeEndDate}</DateFormat>
            </td>
          </tr>
        </tbody>
      </Table>

      <Box>
        <Button component={Link} to={link(wpEditOptionsSubsLink(warrantProgram))}>
          <Trans id="Continue" />
        </Button>
      </Box>
    </Stack>
  );
};
